import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { Helper, PrimaryButton, TextField } from "../components/ui";
import { AuthSubmit } from "../components/AuthSubmit";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { humanizeAuthError } from "../authErrors";
import { isValidEmail } from "../validation";
import { useI18n } from "../i18n";
import { useAuthNav } from "../authTransition";
import { useFormError } from "../useFormError";
import "./auth.css";

export default function ForgotPasswordPage() {
  const nav = useNavigate();
  const { go } = useAuthNav();
  const { user, pendingEmail } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState(pendingEmail ?? "");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const { error, setError, formRef } = useFormError();
  const [cooldown, setCooldown] = useState(0);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    if (user) nav("/overview", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    if (cooldown <= 0) {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    if (tickRef.current !== null) return;
    tickRef.current = window.setInterval(() => {
      setCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [cooldown]);

  const send = async () => {
    const normalized = email.trim();
    if (!isValidEmail(normalized)) {
      setError(t("auth.invalidEmail"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const resp = await api.startPasswordRecovery(normalized);
      setSentTo(normalized);
      setCooldown(Math.max(0, Number(resp.cooldown_remaining_seconds ?? 0)));
    } catch (err) {
      setError(humanizeAuthError(err, t, "auth.forgotFailed"));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || cooldown > 0) return;
    await send();
  };

  return (
    <AuthLayout title={t("auth.forgotTitle")} showBack>
      <form ref={formRef} className="auth__form anim-stagger" onSubmit={onSubmit} noValidate>
        {sentTo ? (
          <>
            <Helper>{t("auth.forgotSent", { email: sentTo })}</Helper>
            <PrimaryButton type="submit" disabled={busy || cooldown > 0}>
              {busy
                ? t("auth.forgotSending")
                : cooldown > 0
                  ? t("auth.forgotResendIn", { seconds: cooldown })
                  : t("auth.forgotResend")}
            </PrimaryButton>
          </>
        ) : (
          <>
            <Helper>{t("auth.forgotIntro")}</Helper>
            <TextField
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.emailPlaceholder")}
              focused
              autoFocus
              disabled={busy}
            />
            <AuthSubmit busy={busy}>
              {busy ? t("auth.forgotSending") : t("auth.forgotSubmit")}
            </AuthSubmit>
          </>
        )}
        {error ? <p className="auth-error">{error}</p> : null}
        <p className="auth-forgot">
          <Link
            to="/"
            onClick={(e) => {
              e.preventDefault();
              go("/", "back");
            }}
          >
            {t("auth.backToLogin")}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
